/**
 * 필터 저장소 유틸리티
 *
 * 페이지 이동 시에는 필터를 유지하지만,
 * 페이지를 닫고 다시 열었을 때는 초기화됩니다.
 */

interface FilterStorageData<T> {
    filters: T;
    timestamp: number;
    pageSessionId: string;
}

// 페이지별 세션 ID를 저장하는 키의 접미사
const PAGE_SESSION_SUFFIX = '_page_session_id';

/**
 * 페이지별 세션 ID 가져오기 또는 생성
 */
function getOrCreatePageSessionId(pageKey: string): string {
    const sessionKey = `${pageKey}${PAGE_SESSION_SUFFIX}`;
    let sessionId = sessionStorage.getItem(sessionKey);

    if (!sessionId) {
        // 새로운 세션 ID 생성
        sessionId = `page_session_${Date.now()}_${Math.random()}`;
        sessionStorage.setItem(sessionKey, sessionId);
    }

    return sessionId;
}

/**
 * 페이지 세션 초기화 (페이지가 마운트될 때 호출)
 * 이전 세션이 있다면 필터를 유지하고, 없다면 새 세션 시작
 */
export function initPageSession(pageKey: string): boolean {
    const sessionKey = `${pageKey}${PAGE_SESSION_SUFFIX}`;
    const existingSession = sessionStorage.getItem(sessionKey);

    if (!existingSession) {
        // 새로운 세션 ID 생성
        const newSessionId = `page_session_${Date.now()}_${Math.random()}`;
        sessionStorage.setItem(sessionKey, newSessionId);
        return false; // 새 세션 시작
    }

    return true; // 기존 세션 유지
}

/**
 * 페이지 세션 종료 (페이지가 언마운트될 때 호출)
 */
export function endPageSession(pageKey: string): void {
    const sessionKey = `${pageKey}${PAGE_SESSION_SUFFIX}`;
    sessionStorage.removeItem(sessionKey);
    sessionStorage.removeItem(pageKey);
}

/**
 * 필터를 sessionStorage에 저장
 */
export function saveFilters<T>(key: string, filters: T): void {
    const pageSessionId = getOrCreatePageSessionId(key);
    const data: FilterStorageData<T> = {
        filters,
        timestamp: Date.now(),
        pageSessionId
    };
    sessionStorage.setItem(key, JSON.stringify(data));
}

/**
 * sessionStorage에서 필터를 불러옴
 * 같은 페이지 세션 내에서만 필터를 복원하고, 새로운 세션이면 null 반환
 */
export function loadFilters<T>(key: string): T | null {
    try {
        const saved = sessionStorage.getItem(key);
        if (!saved) return null;

        const data: FilterStorageData<T> = JSON.parse(saved);
        const sessionKey = `${key}${PAGE_SESSION_SUFFIX}`;
        const currentPageSessionId = sessionStorage.getItem(sessionKey);

        // 페이지 세션 ID가 없거나 다르면 null 반환
        if (!currentPageSessionId || data.pageSessionId !== currentPageSessionId) {
            sessionStorage.removeItem(key);
            return null;
        }

        return data.filters;
    } catch {
        return null;
    }
}

/**
 * 필터를 sessionStorage에서 삭제
 */
export function clearFilters(key: string): void {
    sessionStorage.removeItem(key);
    const sessionKey = `${key}${PAGE_SESSION_SUFFIX}`;
    sessionStorage.removeItem(sessionKey);
}
